import { useRef, useState } from 'react';
import type { OrderComment } from '@/infrastructure/FulfillmentAdminClient';
import { MentionWarning } from '@/pages/fulfillment/components/MentionWarning';
import { isComposingKey, useMentionSuggest } from '@/pages/fulfillment/components/MentionSuggest';
import { kstLogStamp } from '@/utils/fulfillmentTime';

import { withMentions } from '@/pages/fulfillment/components/mentionText';

/** The warehouse reads Seoul clocks, so a comment never shifts with the operator's own [T09]. */
const stamp = (iso: string): string => kstLogStamp(iso) ?? iso;

export interface RowCommentsProps {
  comments: OrderComment[];
  busy: boolean;
  onPost: (text: string) => Promise<void>;
}

/** The order's thread, opened under its own row so the picker never leaves the queue. */
export function RowComments({ comments, busy, onPost }: RowCommentsProps) {
  const [draft, setDraft] = useState('');
  const mention = useMentionSuggest<HTMLInputElement>({ value: draft, onChange: setDraft });
  const postingRef = useRef(false);
  const [posting, setPosting] = useState(false);

  const post = async () => {
    const text = draft.trim();
    if (!text || busy || postingRef.current) {
      return;
    }
    postingRef.current = true;
    setPosting(true);
    try {
      await onPost(text);
      setDraft('');
    } catch {
      // The page reports the refusal; the draft stays so the picker can retry it.
    } finally {
      postingRef.current = false;
      setPosting(false);
    }
  };

  return (
    <>
      {comments.length === 0 && <div className="empty">No comments yet</div>}
      {comments.map((comment) => (
        <div className="c-item" key={`${comment.at}-${comment.author}-${comment.text}`}>
          <span className="who">{comment.author}</span>
          <span>{withMentions(comment.text)}</span>
          <time>{stamp(comment.at)}</time>
        </div>
      ))}
      <div className="cwrite">
        <input
          {...mention.fieldProps}
          aria-label="Write a comment"
          placeholder="Write a comment — @mention sends an automatic Slack notification (order no. · text · time · author)"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (mention.onKeyDown(event)) {
              return;
            }
            // The Enter that confirms a Korean syllable is not a post.
            if (event.key === 'Enter' && !isComposingKey(event)) {
              event.preventDefault();
              void post();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-blue btn-sm"
          disabled={busy || posting || !draft.trim()}
          onClick={() => void post()}
        >
          Post
        </button>
      </div>
      {mention.list}
      <MentionWarning text={draft} typing={mention.typing} />
    </>
  );
}
